import { FC, useCallback, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import {
  Add,
  Edit,
  DeleteOutlined,
  Logout,
  AccountCircle,
  GppMaybe,
  Key as KeyIcon,
  FileDownload,
  TrendingUp,
} from "@mui/icons-material"
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro"
import { useSnackbar } from "notistack"
import { TooltipAction } from "../common/tooltipAction"
import {
  DataGrid,
  Modal,
  RtkHookForm,
  infiniteListboxProps,
} from "@/core/components"
import {
  useUrlListState,
  useUrlFilters,
  useInfiniteSelectList,
  UrlFilterSchema,
} from "@/core/hooks"
import { TextFieldElement, AutocompleteElement } from "react-hook-form-mui"
import { getApiErrorMessage } from "@/core/utils"
import { Userbot } from "../../interfaces"
import {
  useAccountsQuery,
  useClientQuery,
  useDeleteAccountMutation,
  useDeleteClientMutation,
  useClientLogoutMutation,
  useClientLoginCodeMutation,
  useClientLoginPasswordMutation,
  useUpdateClientMutation,
  useLazyProxiesQuery,
} from "../../store"
import { useUserbotWriteAccess, useClientsLookup } from "../../hooks"
import { ConfirmDialog } from "../common/confirmDialog"
import { ClientPolicy } from "../common/clientPolicy"
import { BatchLaunchWizard } from "../batches/batchLaunchWizard"
import { AccountForm } from "./accountForm"
import { PromoteClientDialog } from "./promoteClientDialog"
import { ClientForm } from "../clients/clientForm"
import { ClientCreateDialog } from "../clients/clientCreateDialog"
import { ClientMeView } from "../clients/clientMeView"
import { SessionExportSection } from "../clients/sessionExportSection"
import { SpamCheckDialog } from "../clients/spamCheckDialog"
import { AccountIdentity } from "../identity/accountIdentity"
import { ClientIdentity } from "../identity/clientIdentity"
import { OnlineDot } from "../identity/onlineDot"
import { LeaseChips } from "../identity/leaseChips"
import { buildFeedRows } from "./feedRows"
import { nextLoginStep, LoginStep } from "../clients/loginMachine"

const SEARCH_SCHEMA = {
  search: "string",
} as const satisfies UrlFilterSchema

const ClientEditModal: FC<{
  client: Userbot.FeedClient
  onClose: () => void
}> = ({ client, onClose }) => {
  const { getClient } = useClientsLookup()
  const cached = getClient(client.id)
  const { data: fetched, isLoading } = useClientQuery(
    { id: client.id },
    { skip: !!cached },
  )

  const fullClient = cached ?? fetched

  if (isLoading) {
    return (
      <Modal
        disableBackdropClose
        title={`Редактирование: ${client.name}`}
        onClose={onClose}
        element={
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        }
      />
    )
  }

  if (fullClient) {
    return (
      <Modal
        disableBackdropClose
        title={`Редактирование: ${fullClient.name}`}
        onClose={onClose}
        element={<ClientForm client={fullClient} onDone={onClose} />}
      />
    )
  }

  return (
    <Modal
      disableBackdropClose
      title={`Редактирование: ${client.name}`}
      onClose={onClose}
      element={<ClientDegradedEditForm client={client} onDone={onClose} />}
    />
  )
}

const ClientDegradedEditForm: FC<{
  client: Userbot.FeedClient
  onDone: () => void
}> = ({ client, onDone }) => {
  const [lazyProxies] = useLazyProxiesQuery()
  const proxiesList = useInfiniteSelectList<Userbot.Proxy>({
    fetchPage: (p) => lazyProxies(p).unwrap(),
    getId: (p) => p.id,
  })

  interface IDegradedFormData {
    name: string
    proxyId?: number | null
  }

  const defaultValues: IDegradedFormData = {
    name: client.name,
    proxyId: client.proxyId ?? null,
  }

  return (
    <RtkHookForm<IDegradedFormData, Userbot.Client>
      successMessage="Сессия обновлена"
      mutationHook={useUpdateClientMutation as never}
      getSubmitHandlerParams={(data) => ({
        id: client.id,
        body: {
          name: data.name,
          proxyId: data.proxyId == null ? null : Number(data.proxyId),
        },
      })}
      afterSubmit={onDone}
      formContextParams={{ defaultValues }}
      actions={[
        {
          text: "Сохранить",
          type: "submit",
          variant: "contained",
          fullWidth: true,
        },
      ]}
    >
      <Stack spacing={2}>
        <Alert severity="info">
          Полные данные сессии недоступны (клиент удалён или ошибка сети).
          Доступно редактирование имени и прокси.
        </Alert>
        <TextFieldElement<IDegradedFormData>
          name="name"
          label="Имя сессии"
          required
        />
        <AutocompleteElement<IDegradedFormData>
          name="proxyId"
          label="Прокси"
          matchId
          loading={proxiesList.isFetching}
          options={proxiesList.items}
          autocompleteProps={{
            fullWidth: true,
            getOptionLabel: (o: Userbot.Proxy) =>
              o ? `${o.label ? o.label + " — " : ""}${o.host}:${o.port}` : "",
            ...infiniteListboxProps({
              hasMore: proxiesList.hasMore,
              isFetching: proxiesList.isFetching,
              onLoadMore: proxiesList.loadMore,
            }),
            renderOption: (props, o: Userbot.Proxy, state) =>
              [
                props,
                `${o.label ? o.label + " — " : ""}${o.host}:${o.port}`,
                state,
              ] as React.ReactNode,
          }}
        />
        <TextField
          label="Модель устройства"
          value="—"
          disabled
          helperText="Полные данные сессии недоступны"
        />
        <TextField
          label="API ID"
          value="—"
          disabled
          helperText="Полные данные сессии недоступны"
        />
      </Stack>
    </RtkHookForm>
  )
}

export const AccountsFeedTable: FC = () => {
  const { read: readFilters, write: writeFilters } =
    useUrlFilters(SEARCH_SCHEMA)
  const { search } = readFilters()
  const list = useUrlListState({
    defaultSortBy: "createdAt",
    defaultOrder: "DESC",
  })
  const { enqueueSnackbar } = useSnackbar()
  const canWrite = useUserbotWriteAccess()

  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(
    new Set(),
  )
  const [wizardOpen, setWizardOpen] = useState(false)

  const { data, isFetching, isError, error } = useAccountsQuery(
    {
      ...list.listArgs,
      search: search || undefined,
    },
    { pollingInterval: 60000 },
  )

  const [deleteAccount, { isLoading: isDeletingAccount }] =
    useDeleteAccountMutation()
  const [deleteClient, { isLoading: isDeletingClient }] =
    useDeleteClientMutation()
  const [logoutClient, { isLoading: isLoggingOutClient }] =
    useClientLogoutMutation()

  const [creatingSession, setCreatingSession] = useState(false)
  const [editingAccount, setEditingAccount] =
    useState<Userbot.AccountFeedItem | null>(null)
  const [deletingAccount, setDeletingAccount] =
    useState<Userbot.AccountFeedItem | null>(null)

  const [editingClient, setEditingClient] = useState<Userbot.FeedClient | null>(
    null,
  )
  const [meClient, setMeClient] = useState<Userbot.FeedClient | null>(null)
  const [exportingSession, setExportingSession] =
    useState<Userbot.FeedClient | null>(null)
  const [spamCheckClient, setSpamCheckClient] =
    useState<Userbot.FeedClient | null>(null)
  const [logoutClientTarget, setLogoutClientTarget] =
    useState<Userbot.FeedClient | null>(null)
  const [deletingClientTarget, setDeletingClientTarget] =
    useState<Userbot.FeedClient | null>(null)
  const [promoteTarget, setPromoteTarget] = useState<Userbot.FeedClient | null>(
    null,
  )
  const closePromote = useCallback(() => setPromoteTarget(null), [])

  const selectedIds = useMemo(() => [...selectedClientIds], [selectedClientIds])
  const closeWizard = useCallback(() => setWizardOpen(false), [])
  const handleLaunched = useCallback(() => {
    setSelectedClientIds(new Set())
    setWizardOpen(false)
  }, [])

  const [loginClientTarget, setLoginClientTarget] =
    useState<Userbot.FeedClient | null>(null)
  const [loginStep, setLoginStep] = useState<LoginStep>("code")
  const [loginCode, setLoginCode] = useState("")
  const [loginPassword, setLoginPassword] = useState("")
  const [loginError, setLoginError] = useState<string | null>(null)
  const [sendCode, { isLoading: isSendingCode }] = useClientLoginCodeMutation()
  const [sendPassword, { isLoading: isSendingPassword }] =
    useClientLoginPasswordMutation()

  const feedRows = useMemo(
    () => buildFeedRows(data?.items ?? []),
    [data?.items],
  )

  // rowId клиентской строки = `${accountKey}:${client.id}` (feedRows.ts) —
  // для выделения нужен явный маппинг rowId → clientId (спека §3).
  const clientRowByClientId = useMemo(() => {
    const m = new Map<string, string>()
    feedRows.forEach((row) => {
      if (row.kind === "client") m.set(row.item.id, row.rowId)
    })
    return m
  }, [feedRows])

  const rowSelectionModel: GridRowSelectionModel = useMemo(() => {
    const out: string[] = []
    clientRowByClientId.forEach((rowId, clientId) => {
      if (selectedClientIds.has(clientId)) out.push(rowId)
    })
    return out
  }, [clientRowByClientId, selectedClientIds])

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const selectedRowIds = new Set(model.map(String))
      setSelectedClientIds((prev) => {
        const next = new Set(prev)
        clientRowByClientId.forEach((rowId, clientId) => {
          if (selectedRowIds.has(rowId)) next.add(clientId)
          else next.delete(clientId)
        })
        return next
      })
    },
    [clientRowByClientId],
  )

  const isRowSelectable = useCallback(
    (p: { row: Userbot.FeedRow }) => p.row.kind === "client",
    [],
  )

  // Стабильная идентичность обязательна: новая инлайн-функция на каждом рендере
  // заставляет MUI X пересобирать дерево строк и сбрасывать раскрытие.
  const getTreeDataPath = useCallback((row: Userbot.FeedRow) => {
    if (row.kind === "account") {
      return [row.accountKey]
    }
    return [row.parentRowId, row.item.id]
  }, [])

  const handleSearchChange = (value: string) => {
    writeFilters({ search: value || undefined })
    list.setPaginationModel({ page: 0, pageSize: list.pageSize })
  }

  const handleFinishLogin = async () => {
    if (!loginClientTarget) return
    setLoginError(null)
    try {
      if (loginStep === "code") {
        const res = await sendCode({
          id: loginClientTarget.id,
          body: { code: loginCode },
        }).unwrap()
        const next = nextLoginStep("code", {
          kind: "code",
          needsPassword: res.needsPassword,
        })
        if (next === "done") {
          setLoginClientTarget(null)
        } else {
          setLoginStep(next)
        }
      } else {
        const res = await sendPassword({
          id: loginClientTarget.id,
          body: { password: loginPassword },
        }).unwrap()
        const next = nextLoginStep("password", {
          kind: "password",
          needsPassword: res.needsPassword,
        })
        if (next === "done") {
          setLoginClientTarget(null)
        } else {
          setLoginStep(next)
        }
      }
    } catch (err) {
      setLoginError(getApiErrorMessage(err))
    }
  }

  const columns: GridColDef<Userbot.FeedRow>[] = useMemo(
    () => [
      {
        field: "username",
        headerName: "Аккаунт",
        flex: 3,
        renderCell: ({ row }) =>
          row.kind === "account" ? (
            <AccountIdentity account={row.item} compact />
          ) : (
            <ClientIdentity
              client={row.item}
              accountOverride={row.account}
              compact
            />
          ),
      },
      {
        field: "accountScope",
        headerName: "Account scope",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "account"
            ? (row.item.id ?? "не привязан")
            : (row.account.id ?? "не привязан"),
      },
      {
        field: "policy",
        headerName: "Политика сессии",
        flex: 4,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "client" ? (
            <ClientPolicy
              client={row.item}
              showScope={false}
              showHelp={false}
            />
          ) : (
            "Политика определяется для каждой сессии"
          ),
      },
      {
        field: "sessions",
        headerName: "Сессии",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.kind === "account") {
            if (row.item.id === null) {
              return (
                <Typography variant="body2" color="warning.main">
                  Ожидает входа · {row.item.clients.length}
                </Typography>
              )
            }
            const onlineCount = row.item.clients.filter(
              (c) => c.isOnline,
            ).length
            const totalClients = row.item.clients.length
            return (
              <Typography variant="body2">
                {totalClients === 1 ? "1 сессия" : `${totalClients} сессий`} ·{" "}
                {onlineCount} онлайн
              </Typography>
            )
          }

          const isDead =
            !row.item.isAuthorized && row.item.authorizedAt !== null
          const isPending =
            !row.item.isAuthorized && row.item.authorizedAt === null

          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <OnlineDot isOnline={row.item.isOnline} />
              {isDead ? (
                <Chip size="small" color="error" label="Сессия протухла" />
              ) : isPending ? (
                <Chip size="small" color="warning" label="Ожидает логина" />
              ) : null}
            </Stack>
          )
        },
      },
      {
        field: "createdAt",
        headerName: "Создан",
        flex: 2,
        valueGetter: ({ row }) => {
          const dt = row.item.createdAt
          return dt ? new Date(dt).toLocaleString("ru") : "—"
        },
      },
      {
        field: "updatedAt",
        headerName: "Обновлён",
        flex: 2,
        valueGetter: ({ row }) => {
          const dt =
            row.kind === "account"
              ? row.item.updatedAt
              : (row.item.lastOnlineAt ?? row.item.updatedAt)
          return dt ? new Date(dt).toLocaleString("ru") : "—"
        },
      },
      {
        field: "leases",
        headerName: "Leases",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "client" ? (
            <LeaseChips leases={row.item.activeLeases} />
          ) : (
            "—"
          ),
      },
    ],
    [],
  )

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Поиск по телефону, username..."
            value={search ?? ""}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ minWidth: 260 }}
          />
        </Stack>

        {canWrite && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreatingSession(true)}
          >
            Добавить сессию
          </Button>
        )}

        {canWrite && (
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2">
              {`Выбрано: ${selectedClientIds.size}`}
            </Typography>
            <Button
              size="small"
              disabled={selectedClientIds.size === 0}
              onClick={() => setSelectedClientIds(new Set())}
            >
              Снять все
            </Button>
            <Button variant="contained" onClick={() => setWizardOpen(true)}>
              {`Запустить действие (${selectedClientIds.size})`}
            </Button>
          </Box>
        )}
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}

      <Box height="78vh">
        <DataGrid<Userbot.FeedRow>
          treeData
          getTreeDataPath={getTreeDataPath}
          checkboxSelection
          isRowSelectable={isRowSelectable}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          disableChildrenSorting
          columns={columns}
          rows={feedRows}
          getRowId={(row) => row.rowId}
          getRowHeight={() => "auto"}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          filterMode="client"
          {...list.gridProps}
          rowActions={[
            (r) => {
              if (r.row.kind === "account") {
                if (r.row.item.id === null || !canWrite) return <></>
                const account = r.row.item
                return (
                  <TooltipAction
                    label="Редактировать"
                    icon={<Edit />}
                    onClick={() => setEditingAccount(account)}
                  />
                )
              }
              const client = r.row.item
              return (
                <TooltipAction
                  label="Профиль"
                  icon={<AccountCircle />}
                  onClick={() => setMeClient(client)}
                />
              )
            },
            (r) => {
              if (r.row.kind === "account") {
                if (r.row.item.id === null || !canWrite) return <></>
                const account = r.row.item
                return (
                  <TooltipAction
                    label="Удалить"
                    icon={<DeleteOutlined />}
                    onClick={() => setDeletingAccount(account)}
                  />
                )
              }
              if (!canWrite) return <></>
              const client = r.row.item
              return (
                <TooltipAction
                  label="Spam-check"
                  icon={<GppMaybe />}
                  onClick={() => setSpamCheckClient(client)}
                />
              )
            },
            (r) => {
              if (r.row.kind === "account") return <></>
              const client = r.row.item
              return (
                <TooltipAction
                  label="Экспорт сессии"
                  icon={<FileDownload />}
                  onClick={() => setExportingSession(client)}
                />
              )
            },
            (r) => {
              if (r.row.kind === "account" || !canWrite) return <></>
              const isPending =
                r.row.account.id === null || r.row.account.pendingLogin === true
              const client = r.row.item
              if (isPending) {
                return (
                  <TooltipAction
                    label="Продолжить вход"
                    icon={<KeyIcon />}
                    onClick={() => {
                      setLoginClientTarget(client)
                      setLoginStep("code")
                      setLoginCode("")
                      setLoginPassword("")
                      setLoginError(null)
                    }}
                  />
                )
              }
              return (
                <TooltipAction
                  label="Редактировать"
                  icon={<Edit />}
                  onClick={() => setEditingClient(client)}
                />
              )
            },
            (r) => {
              if (r.row.kind === "account" || !canWrite) return <></>
              const client = r.row.item
              return (
                <TooltipAction
                  label="Выйти"
                  icon={<Logout />}
                  onClick={() => setLogoutClientTarget(client)}
                />
              )
            },
            (r) => {
              if (r.row.kind === "account" || !canWrite) return <></>
              const client = r.row.item
              return (
                <TooltipAction
                  label="Удалить"
                  icon={<DeleteOutlined />}
                  onClick={() => setDeletingClientTarget(client)}
                />
              )
            },
            (r) => {
              if (r.row.kind === "account" || !canWrite) return <></>
              // Запаска = авторизованный неактивный клиент (дельта C §5).
              const client = r.row.item
              if (!(client.isAuthorized && !client.isActive)) return <></>
              return (
                <TooltipAction
                  label="Продвинуть"
                  icon={<TrendingUp />}
                  onClick={() => setPromoteTarget(client)}
                />
              )
            },
          ]}
        />
      </Box>

      {editingAccount && (
        <Modal
          disableBackdropClose
          title="Редактирование аккаунта"
          onClose={() => setEditingAccount(null)}
          element={
            <AccountForm
              account={editingAccount as unknown as Userbot.Account}
              onDone={() => setEditingAccount(null)}
            />
          }
        />
      )}
      <ConfirmDialog
        open={!!deletingAccount}
        title="Удалить аккаунт?"
        description={
          deletingAccount?.phone ?? deletingAccount?.telegramUserId ?? ""
        }
        loading={isDeletingAccount}
        onCancel={() => setDeletingAccount(null)}
        onConfirm={async () => {
          if (deletingAccount?.id) {
            try {
              await deleteAccount({ id: deletingAccount.id }).unwrap()
              setDeletingAccount(null)
            } catch (err) {
              enqueueSnackbar(getApiErrorMessage(err), { variant: "error" })
            }
          }
        }}
      />

      {creatingSession && (
        <ClientCreateDialog onClose={() => setCreatingSession(false)} />
      )}

      {meClient && (
        <ClientMeView
          client={meClient as unknown as Userbot.Client}
          onClose={() => setMeClient(null)}
        />
      )}

      {exportingSession && (
        <Dialog
          open
          onClose={() => setExportingSession(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Экспорт сессии: {exportingSession.name}</DialogTitle>
          <DialogContent>
            <Box mt={1}>
              <SessionExportSection clientId={exportingSession.id} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportingSession(null)}>Закрыть</Button>
          </DialogActions>
        </Dialog>
      )}

      {spamCheckClient && (
        <SpamCheckDialog
          clientId={spamCheckClient.id}
          clientName={spamCheckClient.name}
          open
          onClose={() => setSpamCheckClient(null)}
        />
      )}

      {editingClient && (
        <ClientEditModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
        />
      )}

      <ConfirmDialog
        open={!!logoutClientTarget}
        title="Выйти из сессии?"
        description={
          logoutClientTarget
            ? `Клиент «${logoutClientTarget.name}» будет разлогинен.`
            : ""
        }
        confirmText="Выйти"
        loading={isLoggingOutClient}
        onCancel={() => setLogoutClientTarget(null)}
        onConfirm={async () => {
          if (logoutClientTarget) {
            try {
              await logoutClient({ id: logoutClientTarget.id }).unwrap()
              setLogoutClientTarget(null)
            } catch (err) {
              enqueueSnackbar(getApiErrorMessage(err), { variant: "error" })
            }
          }
        }}
      />

      <ConfirmDialog
        open={!!deletingClientTarget}
        title="Удалить клиента?"
        description={deletingClientTarget?.name}
        loading={isDeletingClient}
        onCancel={() => setDeletingClientTarget(null)}
        onConfirm={async () => {
          if (deletingClientTarget) {
            try {
              await deleteClient({ id: deletingClientTarget.id }).unwrap()
              setDeletingClientTarget(null)
            } catch (err) {
              enqueueSnackbar(getApiErrorMessage(err), { variant: "error" })
            }
          }
        }}
      />

      {loginClientTarget && (
        <Dialog
          open
          onClose={() => setLoginClientTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Продолжить вход: {loginClientTarget.name}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              {loginError && <Alert severity="error">{loginError}</Alert>}
              {loginStep === "code" ? (
                <TextField
                  label="Код подтверждения"
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value)}
                  fullWidth
                  autoFocus
                />
              ) : (
                <TextField
                  label="Пароль 2FA"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  fullWidth
                  autoFocus
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLoginClientTarget(null)}>Отмена</Button>
            <Button
              variant="contained"
              onClick={handleFinishLogin}
              disabled={
                isSendingCode ||
                isSendingPassword ||
                (loginStep === "code"
                  ? !loginCode.trim()
                  : !loginPassword.trim())
              }
            >
              {loginStep === "code" ? "Отправить код" : "Подтвердить пароль"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {promoteTarget && (
        <PromoteClientDialog client={promoteTarget} onClose={closePromote} />
      )}

      {wizardOpen && (
        <BatchLaunchWizard
          selectedClientIds={selectedIds}
          onClose={closeWizard}
          onLaunched={handleLaunched}
        />
      )}
    </>
  )
}
